import React, { Fragment } from "react"
import { useParams } from "react-router-dom"
import PrimeField from "components/PrimeField/PrimeField"
import { PrimeButton, Tag } from "components-design-system"
import Modal from "components/Modal/Modal"

import { useGetStudy, usePutStudy } from "api/hooks/studies/useStudiesApi"
import "./EditCountriesModal.scss"

const EditCountriesModal = ({ showModal, onHide, variant, countryCodes, countries, studyId }) => {
  const { studyId: study_pk } = useParams()

  const { data: study } = useGetStudy(study_pk)
  const [search, setSearch] = React.useState("")
  const [extended, setExtended] = React.useState(true)
  const [selectedCountries, setSelectedCountries] = React.useState([])
  const [buttonLoading, setButtonLoading] = React.useState(false)

  const putStudy = usePutStudy(study_pk)

  React.useEffect(() => {
    if (countryCodes) init()
  }, [variant, countryCodes])

  const init = () => {
    if (countryCodes) {
      setSelectedCountries(countryCodes)
    }
  }

  const onDeleteCountry = (country) => {
    const foundCountry = Object.keys(countries).find((key) => countries[key].label === country)
    setSelectedCountries(selectedCountries.filter((key) => key !== foundCountry))
  }

  const handleCountry = (evt, country) => {
    let temp = [...selectedCountries]
    if (evt) {
      temp.push(country.value)
    } else {
      const foundIndex = temp.findIndex((key) => key === country.value)
      if (foundIndex !== -1) temp.splice(foundIndex, 1)
    }
    setSelectedCountries(temp)
  }

  const handleCheckAll = (evt) => {
    let selectCountries = []
    if (evt) {
      Object.keys(countries).forEach((code) => {
        selectCountries.push(code)
      })
    }
    setSelectedCountries(selectCountries)
  }

  const onSaveChanges = async () => {
    let request = {
      study_data: {
        ...study.study_data,
      },
    }

    if (variant === "actual") {
      request.study_data.actual_countries = selectedCountries
    } else if (variant === "planned") {
      request.study_data.planned_countries = selectedCountries
    }

    putStudy(
      {
        id: `${study_pk}/`,
        body: request,
      },
      {
        onSuccess: () => {
          onHide(null)
        },
      }
    )
  }

  let breaker = null

  if (!countries) {
    return null
  }

  return (
    <Modal
      subtitle={studyId}
      title={`Edit Study - ${variant === "planned" ? "Planned Countries" : "Actual Countries"}`}
      message={`Select the ${variant} countries where this study will be conducted`}
      open={showModal}
      onCancel={onHide}
      footer={true}
      hideImage={true}
      width={540}
      className="edit-countries-modal"
    >
      <div className="edit-countries-modal-selected">
        <div className="edit-countries-modal-selected-head">
          <p className="edit-countries-modal-selected-heading">{`Selected ${
            variant === "planned" ? "Planned Countries" : "Actual Countries"
          } (${selectedCountries.length})`}</p>
          <p
            className="edit-countries-modal-selected-clear-all"
            onClick={() => handleCheckAll(false)}
          >
            Deselect All
          </p>
          <div
            className="edit-countries-modal-selected-chevron"
            onClick={() => setExtended((state) => !state)}
          >
            {extended ? (
              <i
                className="fal fa-chevron-up"
                style={{ cursor: "pointer" }}
                // onClick={onExtend}
              />
            ) : (
              <i
                className="fal fa-chevron-down"
                style={{ cursor: "pointer" }}
                // onClick={onExtend}
              />
            )}
          </div>
        </div>
        {extended && (
          <div className="edit-countries-modal-selected-body">
            {selectedCountries.map((country) => {
              return (
                <Tag
                  key={country}
                  bgColor="#B3e6ff"
                  text={countries[country].label}
                  onDelete={onDeleteCountry}
                />
              )
            })}
          </div>
        )}
      </div>
      <PrimeField
        schema={{
          type: "text",
          label: "Search Countries",
          placeholder: "Country name",
        }}
        onChange={(evt) => setSearch(evt)}
      />
      <div className="edit-countries-modal-select">
        <div className="edit-countries-modal-check-item">
          <PrimeField
            schema={{
              type: "checkbox",
              label: "Select All Countries",
            }}
            value={selectedCountries.length === Object.keys(countries).length}
            onChange={(evt) => handleCheckAll(evt)}
          />
        </div>
        {Object.keys(countries)
          .map((key) => {
            return {
              value: key,
              label: countries[key].label,
            }
          })
          .sort((a, b) => {
            if (a.label < b.label) return -1 // a comes before b
            if (a.label > b.label) return 1 // a comes after b
            return 0 // a and b are equal
          })
          .map((country) => {
            let isHidden = false
            let shouldBreak = false
            let checkItemStyle = { paddingLeft: 16 }

            if (search && !country?.label?.toLowerCase()?.includes(search?.toLowerCase())) {
              isHidden = true
              checkItemStyle.display = "none"
            }

            if (!isHidden) {
              if (breaker !== country?.label?.[0]) {
                shouldBreak = true
                breaker = country?.label?.[0]
              }
            }

            return (
              <Fragment key={country.cca3}>
                {shouldBreak && (
                  <div className="edit-countries-modal-select-breaker">{country.label?.[0]?.toUpperCase()} </div>
                )}
                <div
                  className="edit-countries-modal-check-item"
                  style={checkItemStyle}
                >
                  <PrimeField
                    schema={{
                      type: "checkbox",
                      label: country?.label,
                    }}
                    value={selectedCountries.includes(country.value)}
                    onChange={(evt) => handleCountry(evt, country)}
                  />
                </div>
              </Fragment>
            )
          })}
      </div>
      <div className="edit-countries-modal-actions">
        <PrimeButton onClick={() => onHide(null)}>Cancel</PrimeButton>
        <PrimeButton
          type="primary"
          disabled={buttonLoading}
          loading={buttonLoading}
          onClick={onSaveChanges}
        >
          Set {variant === "planned" ? "Planned Countries" : "Actual Countries"}
        </PrimeButton>
      </div>
    </Modal>
  )
}

export default EditCountriesModal
